import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import callApi from '../../service/callApi';
import { enqueueSnackbar, somethingWrongNotification } from '../notifier/notifierActions';
import {
  changeLocalMessage,
  setEditableMessage,
  setIsDirty,
  likeLocalMessage,
  setFinalMessagesCount,
} from './scoutReducer';
import { ASSISTANTS_CATEGORY, REACTIONS, SCOUT_CREATE_TYPE, SCOUT_TABS } from '../../constants';
import sendRequest from '../../service/sendRequest';
import { downloadDocument } from '../../utils/downloadDocument';
import { openProject } from '../global/globalReducer';
import { history } from '../../store';
import generateFileName from '../../utils/generateFileName';

const GET_WRITERS = 'scout/GET_WRITERS';
const LOAD_MORE_WRITERS = 'scout/LOAD_MORE_WRITERS';
const SAVE_MESSAGE = 'scout/SAVE_MESSAGE';
const LIKE_MESSAGE = 'scout/LIKE_MESSAGE';
const DELETE_MESSAGE = 'scout/DELETE_MESSAGE';
const GET_ALL_MESSAGES = 'scout/GET_ALL_MESSAGES';
const GET_SELECTED_MESSAGES = 'scout/GET_SELECTED_MESSAGES';
const GET_BACKUP_MESSAGES = 'scout/GET_BACKUP_MESSAGES';
const GENERATE_MESSAGES = 'scout/GENERATE_MESSAGES';
const GET_MESSAGES_LOGS = 'scout/GET_MESSAGES_LOGS';
const CREATE_MESSAGE = 'scout/CREATE_MESSAGE';
const RESET_SESSION = 'scout/RESET_SESSION';
const REPLY_SCOUT = 'scout/REPLY_SCOUT';
const GET_FINAL_MESSAGES = 'scout/GET_FINAL_MESSAGES';
const GET_SCREENED_MESSAGES = 'scout/GET_SCREENED_MESSAGES';
const GET_SCOUT_FORM_DATA = 'scout/GET_SCOUT_DATA';
const SELECT_MESSAGE = 'scout/SELECT_MESSAGE';
const CLEAR_BOARD = 'scout/CLEAR_BOARD';
const DELETE_MULTIPLE_MESSAGES = 'scout/DELETE_MULTIPLE_MESSAGES';
const UPLOAD_FILE = 'scout/UPLOAD_FILE';
const GET_MESSAGES_BY_TAB = 'scout/GET_MESSAGES_BY_TAB';
const EXPORT_MESSAGES = 'scout/EXPORT_MESSAGES';
const CLEAR_ALL = 'scout/CLEAR_ALL';
const GENERATE_MORE = 'scout/GENERATE_MORE';
const COMPRESS_MESSAGES = 'scout/COMPRESS_MESSAGES';
const SEND_RESPONSE_DATA = 'scout/SEND_RESPONSE_DATA';
const REQUEST_FOR_HELP = 'scout/REQUEST_FOR_HELP';
const GET_VARIATION_SCORE = 'scout/GET_VARIATION_SCORE';
const CLEAR_SELECTED = 'scout/CLEAR_SELECTED';
const RECLAIM_BACKUP = 'scout/RECLAIM_BACKUP';
const RESTORE_MESSAGE = 'scout/RESTORE_MESSAGE';

const loadWritersOptions = async ({ search, page = 1, language }) => {
  const response = await sendRequest(() =>
    callApi.post(`/api/settings/search/writer?page=${page}&size=${10}`, {
      ...(search ? { query: search } : {}),
      ...(language ? { language } : {}),
    }),
  );

  return { content: response.data.content, total: response.data.totalElements };
};
export const getWriters = createAsyncThunk(GET_WRITERS, loadWritersOptions);
export const loadMoreWriters = createAsyncThunk(LOAD_MORE_WRITERS, loadWritersOptions);

export const saveMessageChanges = createAsyncThunk(SAVE_MESSAGE, async (_, { dispatch, getState, rejectWithValue }) => {
  const {
    editableMessage: { id, message },
  } = getState().scout;

  dispatch(changeLocalMessage({ id, message }));
  dispatch(setEditableMessage({ id: null, message: '' }));
  dispatch(setIsDirty(false));

  await sendRequest(
    () =>
      callApi.post(`/api/scout-v2/message/edit-message/${id}`, {
        message,
      }),
    { onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue) },
  );
});

export const likeMessage = createAsyncThunk(
  LIKE_MESSAGE,
  async ({ id, reaction, createType }, { dispatch, getState, rejectWithValue }) => {
    dispatch(likeLocalMessage({ id, reaction, createType }));

    await sendRequest(
      () =>
        callApi.post(`/api/scout-v2/message/reaction`, {
          id,
          ...(reaction ? { reaction } : {}),
        }),
      { onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue) },
    );

    return id;
  },
);

export const deleteMessage = createAsyncThunk(DELETE_MESSAGE, async ({ id }, { dispatch }) => {
  await sendRequest(() => callApi.delete(`/api/scout-v2/message/${id}`), {
    onError: () =>
      dispatch(
        enqueueSnackbar({
          message: 'couldntDeleteMessage',
          options: {
            variant: 'error',
          },
        }),
      ),
  });

  dispatch(
    enqueueSnackbar({
      message: 'messageHasBeenDeleted',
      options: {
        variant: 'success',
      },
    }),
  );

  return id;
});

export const deleteMultipleMessages = createAsyncThunk(
  DELETE_MULTIPLE_MESSAGES,
  async (ids, { dispatch, rejectWithValue }) => {
    await sendRequest(() => callApi.delete(`/api/scout-v2/message/delete/by/ids`, { data: ids }), {
      onError: (e) => {
        dispatch(
          enqueueSnackbar({
            message: 'couldntDeleteMessages',
            options: {
              variant: 'error',
            },
          }),
        );
        return rejectWithValue(e);
      },
    });

    dispatch(
      enqueueSnackbar({
        message: 'messagesHasBeenDeleted',
        options: {
          variant: 'success',
        },
      }),
    );

    return [];
  },
);

export const getScoutFormData = createAsyncThunk(GET_SCOUT_FORM_DATA, async ({ id }, { rejectWithValue, dispatch }) => {
  const response = await sendRequest(() => callApi.get(`/api/scout-v2/${id}`), {
    onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
  });

  dispatch(openProject(id));

  return response.data;
});

const loadMessages = async (id, getState, options = {}, dispatch, rejectWithValue) => {
  const { sort, searchQuery } = getState().scout.tableSettings;

  const response = await sendRequest(
    (cancelToken) =>
      callApi.post(
        `/api/scout-v2/message/search?sort=${sort}&id=${id}`,
        {
          ...(searchQuery ? { word: searchQuery } : {}),
          ...options,
        },
        { cancelToken },
      ),
    {
      onError: (e) => {
        if (e.response.status !== 409) {
          dispatch(somethingWrongNotification());
        }
        return rejectWithValue(e);
      },
    },
  );

  return response.data;
};
export const getAllMessages = createAsyncThunk(
  GET_ALL_MESSAGES,
  async ({ id }, { getState, dispatch, rejectWithValue }) =>
    loadMessages(id, getState, { createType: SCOUT_CREATE_TYPE.GENERATED }, dispatch, rejectWithValue),
);
export const getSelectedMessages = createAsyncThunk(
  GET_SELECTED_MESSAGES,
  async ({ id }, { getState, dispatch, rejectWithValue }) =>
    loadMessages(id, getState, { selected: true, createType: SCOUT_CREATE_TYPE.GENERATED }, dispatch, rejectWithValue),
);
export const getBackupMessages = createAsyncThunk(
  GET_BACKUP_MESSAGES,
  async ({ id }, { getState, dispatch, rejectWithValue }) =>
    loadMessages(id, getState, { status: 'BACKUP' }, dispatch, rejectWithValue),
);
export const getFinalMessages = createAsyncThunk(
  GET_FINAL_MESSAGES,
  async ({ id }, { getState, dispatch, rejectWithValue }) => {
    dispatch(getVariationScore(id));

    const compressedMessages = await loadMessages(
      id,
      getState,
      {
        reaction: REACTIONS.LIKE,
        createType: SCOUT_CREATE_TYPE.COMPRESSED,
      },
      dispatch,
      rejectWithValue,
    );
    const likedMessages = await loadMessages(
      id,
      getState,
      {
        reaction: REACTIONS.LIKE,
        createType: SCOUT_CREATE_TYPE.GENERATED,
      },
      dispatch,
      rejectWithValue,
    );

    return [...compressedMessages, ...likedMessages];
  },
);
export const getScreenedMessages = createAsyncThunk(GET_SCREENED_MESSAGES, async ({ id }, { getState }) => {
  const compressedMessages = await loadMessages(id, getState, {
    reactionNot: REACTIONS.LIKE,
    createType: SCOUT_CREATE_TYPE.COMPRESSED,
  });
  const generatedMessages = await loadMessages(id, getState, {
    reactionNot: REACTIONS.LIKE,
    selected: true,
    createType: SCOUT_CREATE_TYPE.GENERATED,
  });
  return [...compressedMessages, ...generatedMessages];
});

export const getMessageLogs = createAsyncThunk(
  GET_MESSAGES_LOGS,
  async ({ messageId }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(() => callApi.get(`/api/scout-v2/message/history/${messageId}`), {
      onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
    });

    return response.data;
  },
);

export const createMessage = createAsyncThunk(
  CREATE_MESSAGE,
  async ({ messages, projectId }, { dispatch, rejectWithValue, getState }) => {
    const messagesToCreate = messages.map(({ message }) => message);
    const scoutTab = getState().scout.scoutTab;

    if (!messagesToCreate.length) {
      return rejectWithValue(null);
    }

    await sendRequest(
      () =>
        callApi.post(`/api/scout-v2/message/create`, {
          messages: messagesToCreate,
          projectId,
          selected: scoutTab === SCOUT_TABS.SELECTED_MESSAGES || scoutTab === SCOUT_TABS.SCREENED_MESSAGES,
          ...(scoutTab === SCOUT_TABS.FINAL_MESSAGES ? { reaction: REACTIONS.LIKE } : {}),
        }),
      {
        successStatusCode: 201,
      },
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(
      enqueueSnackbar({
        message: 'messageWasCreatedSuccessfully',
        options: {
          variant: 'success',
        },
      }),
    );

    dispatch(getMessagesByTab());
  },
);

export const resetSession = createAsyncThunk(RESET_SESSION, async (id, { rejectWithValue, dispatch }) => {
  const response = await sendRequest(() => callApi.get(`/api/scout-v2/reset/session/${id}`), {
    onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
  });

  return response.data;
});

export const replyScout = createAsyncThunk(
  REPLY_SCOUT,
  async ({ id, reply }, { rejectWithValue, dispatch, getState }) => {
    const scoutTab = getState().scout.scoutTab;

    let response;

    if (scoutTab === SCOUT_TABS.ALL_MESSAGES) {
      response = await sendRequest(() => callApi.post(`/api/scout-v2/reply`, { projectId: id, reply }), {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      });
    }

    if (scoutTab === SCOUT_TABS.SELECTED_MESSAGES) {
      response = await sendRequest(() => callApi.post(`/api/scout-v2/reply/selected`, { projectId: id, reply }), {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      });
    }

    const storedProjectId = getState().scout.projectId;

    if (id !== storedProjectId) {
      return rejectWithValue(null);
    }

    dispatch(getMessagesByTab());

    return response.data;
  },
);

export const generateMessages = createAsyncThunk(
  GENERATE_MESSAGES,
  async ({ id, values }, { rejectWithValue, dispatch, getState }) => {
    const { byPass, focusOn, url, channels, fileText } = values;

    const assistantId = channels[ASSISTANTS_CATEGORY.GENERATE_ALL].assistant?.id;

    if (!id || !assistantId || !url) {
      return rejectWithValue(null);
    }

    const response = await sendRequest(
      (cancelToken) =>
        callApi.post(
          `/api/scout-v2/generate/messages`,
          {
            projectId: id,
            assistantId,
            byPass,
            focusOn,
            url,
            fileText,
          },
          { cancelToken },
        ),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    const storedProjectId = getState().scout.projectId;

    if (id !== storedProjectId) {
      return rejectWithValue(null);
    }

    dispatch(getMessagesByTab());

    return response.data;
  },
);

export const selectMessage = createAsyncThunk(
  SELECT_MESSAGE,
  async ({ id, selected }, { rejectWithValue, dispatch }) => {
    const response = await sendRequest(
      () => callApi.post(`/api/scout-v2/message/${selected ? 'unselect' : 'select'}/${id}`),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    return { id, selected: !selected, createType: response.data.createType };
  },
);

export const clearBoard = createAsyncThunk(CLEAR_BOARD, async (_, { getState, dispatch }) => {
  const scoutTab = getState().scout.scoutTab;

  if (scoutTab === SCOUT_TABS.SELECTED_MESSAGES) {
    dispatch(clearBoardSelected());
    return null;
  }

  const boardMessagesIds = getState().scout.messagesContent.map(({ id }) => id);
  dispatch(deleteMultipleMessages(boardMessagesIds));
});

export const clearBoardSelected = createAsyncThunk(
  CLEAR_SELECTED,
  async (_, { getState, rejectWithValue, dispatch }) => {
    const storedProjectId = getState().scout.projectId;

    const response = await sendRequest(() => callApi.post(`/api/scout-v2/clear/selected?id=${storedProjectId}`), {
      onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
    });

    return response.data;
  },
);

export const uploadFile = createAsyncThunk(UPLOAD_FILE, async (file, { rejectWithValue }) => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  const response = await sendRequest(
    () =>
      axios.request({
        url: `/api/util/parse/file`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        method: 'POST',
        data: formData,
      }),
    {
      onError: (e) => {
        somethingWrongNotification();

        return rejectWithValue(e);
      },
    },
  );

  return response.data;
});

export const getMessagesByTab = createAsyncThunk(
  GET_MESSAGES_BY_TAB,
  async (id, { getState, dispatch, rejectWithValue }) => {
    const scoutTab = getState().scout.scoutTab;
    const storedProjectId = getState().scout.projectId;
    const projectId = id || storedProjectId;

    if (!projectId) {
      return rejectWithValue(null);
    }

    if (scoutTab === SCOUT_TABS.ALL_MESSAGES) {
      dispatch(getAllMessages({ id: projectId }));
    }

    if (scoutTab === SCOUT_TABS.SELECTED_MESSAGES) {
      dispatch(getSelectedMessages({ id: projectId }));
    }

    if (scoutTab === SCOUT_TABS.FINAL_MESSAGES) {
      dispatch(getFinalMessages({ id: projectId }));
    }

    if (scoutTab === SCOUT_TABS.SCREENED_MESSAGES) {
      dispatch(getScreenedMessages({ id: projectId }));
    }

    if (scoutTab === SCOUT_TABS.BACKUP) {
      dispatch(getBackupMessages({ id: projectId }));
    }
  },
);

export const exportMessages = createAsyncThunk(EXPORT_MESSAGES, async (_, { rejectWithValue, getState, dispatch }) => {
  const messages = getState().scout.messagesContent.map(({ id }) => id);
  const projectName = getState().scout.projectName;

  if (!messages.length) {
    dispatch(
      enqueueSnackbar({
        message: 'noMessagesAvailable',
        options: {
          variant: 'warning',
          subMessage: 'pleaseGenerateMessagesBefore',
        },
      }),
    );
    return rejectWithValue(null);
  }

  const response = await sendRequest(
    () =>
      callApi.request({
        url: `/api/scout-v2/message/export`,
        method: 'POST',
        responseType: 'blob',
        data: messages,
      }),
    {
      onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
    },
  );

  const fileName = generateFileName({ name: projectName, ext: 'txt' });

  downloadDocument(fileName, response.data);
});

export const clearAll = createAsyncThunk(CLEAR_ALL, async (projectId, { rejectWithValue, dispatch, getState }) => {
  const response = await sendRequest(() => callApi.post(`/api/scout-v2/clear?id=${projectId}`), {
    onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
  });

  const storedProjectId = getState().scout.projectId;

  if (projectId !== storedProjectId) {
    return rejectWithValue(null);
  }

  dispatch(getMessagesByTab(projectId));

  return response.data;
});

export const reclaimBackup = createAsyncThunk(
  RECLAIM_BACKUP,
  async (projectId, { rejectWithValue, dispatch, getState }) => {
    const response = await sendRequest(() => callApi.post(`/api/scout-v2/message/reclaim?projectId=${projectId}`), {
      onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
    });

    const storedProjectId = getState().scout.projectId;

    if (projectId !== storedProjectId) {
      return rejectWithValue(null);
    }

    dispatch(getMessagesByTab(projectId));

    return response.data;
  },
);

export const restoreMessage = createAsyncThunk(RESTORE_MESSAGE, async ({ id }, { rejectWithValue, dispatch }) => {
  await sendRequest(() => callApi.post(`/api/scout-v2/message/restore-message?messageId=${id}`), {
    onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
  });
  return id;
});

export const generateMore = createAsyncThunk(
  GENERATE_MORE,
  async ({ id, values }, { rejectWithValue, dispatch, getState }) => {
    const { byPass, focusOn, url, channels, fileText } = values;

    const assistantId = channels[ASSISTANTS_CATEGORY.GENERATE_SELECTED].assistant?.id;

    if (!id || !assistantId || !url) {
      return rejectWithValue(null);
    }

    const response = await sendRequest(
      (cancelToken) =>
        callApi.post(
          `/api/scout-v2/generate/selected/messages`,
          {
            projectId: id,
            assistantId,
            byPass,
            focusOn,
            url,
            fileText,
          },
          { cancelToken },
        ),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    const storedProjectId = getState().scout.projectId;

    if (id !== storedProjectId) {
      return rejectWithValue(null);
    }

    dispatch(getMessagesByTab());

    return response.data;
  },
);

export const compressMessages = createAsyncThunk(
  COMPRESS_MESSAGES,
  async ({ id, values }, { rejectWithValue, dispatch, getState }) => {
    const count = getState().scout.targetNumber;
    const { channels } = values;

    const assistantId = channels[ASSISTANTS_CATEGORY.COMPRESS].assistant?.id;

    if (!id || !assistantId) {
      return rejectWithValue(null);
    }

    const response = await sendRequest(
      () => callApi.post(`/api/scout-v2/compress?projectId=${id}&count=${count}&assistantId=${assistantId}`),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    const storedProjectId = getState().scout.projectId;

    if (id !== storedProjectId) {
      return rejectWithValue(null);
    }

    dispatch(getMessagesByTab());

    return response.data;
  },
);

export const sendResponseData = createAsyncThunk(
  SEND_RESPONSE_DATA,
  async (values, { rejectWithValue, dispatch, getState }) => {
    const {
      ageFrom,
      ageTo,
      company,
      email,
      gender,
      market,
      otherRequirements,
      region,
      respondentVolume,
      surveyTemplate,
    } = values;
    const messagesIds = getState().scout.collectDataMessages.map(({ id }) => id);

    await sendRequest(
      () =>
        callApi.post(`/api/scout-v2/send`, {
          ageFrom,
          ageTo,
          company,
          email,
          gender,
          market,
          otherRequirements,
          region,
          respondentVolume,
          surveyTemplate,
          messagesIds,
        }),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
        onSuccess: () => {
          dispatch(
            enqueueSnackbar({
              message: 'emailHasBeenSent',
              options: {
                variant: 'success',
              },
            }),
          );
        },
      },
    );
  },
);

export const requestForHelp = createAsyncThunk(REQUEST_FOR_HELP, async (values, { rejectWithValue, dispatch }) => {
  const { email, question } = values;

  await sendRequest(() => callApi.post(`/api/scout-v2/help`, { email, question }), {
    onError: (e) => {
      dispatch(somethingWrongNotification());
      return rejectWithValue(e);
    },
    onSuccess: () => {
      dispatch(
        enqueueSnackbar({
          message: 'helpRequestSubmitted',
          options: {
            variant: 'success',
            subMessage: 'weeWillTouchYou',
          },
        }),
      );
    },
  });
});

export const getVariationScore = createAsyncThunk(GET_VARIATION_SCORE, async (id, { rejectWithValue }) => {
  const response = await sendRequest(() => callApi.get(`/api/scout-v2/variation/score/${id}`), {
    onError: rejectWithValue(null),
  });

  return response.data;
});

const defaultErrorHandler = (e, dispatch, rejectWithValue) => {
  if (e.response.status === 409) {
    dispatch(
      enqueueSnackbar({
        message: 'projectInUse',
        options: {
          variant: 'warning',
          subMessage: 'thisProjectIsCurrentlyUsing',
          subMessageParams: {
            username: e.response.data.username ?? '',
          },
        },
      }),
    );

    history.push('/projects');
    return rejectWithValue(e);
  }

  dispatch(somethingWrongNotification());
  return rejectWithValue(e);
};
